import React, { Component } from 'react'
import { Skeleton, Row, Col, Space, Form, Input, Switch, Select } from 'antd'
import slugify from 'slugify'
import TableTransfer from '../../../TableTransfer'
import ICountry from '../../../../interfaces/ICountry'
import ILanguage from '../../../../interfaces/ILanguage'
import { SearchRequest, ShelterVerifySlugRequest } from '../../../../services/api/interfaces/ApiRequest'

type GeneralFormProps = {
  form: any,
  validSlug: boolean | null,
  countries: ICountry[] | null,
  languages: ILanguage[] | null,

  contentLoading?: boolean,

  verifySlug: (args: ShelterVerifySlugRequest) => Promise<boolean>,

  loadCountries: (args: SearchRequest) => void,
  loadLanguages: (args: SearchRequest) => void
}

export default class GeneralForm extends Component<GeneralFormProps> {

  static defaultProps = {
    contentLoading: true
  }

  state = {
    mainLanguage: '',
    selectedLanguages: [] as any[],
    selectionLanguages: {
      sourceSelectedKeys: [] as any[],
      targetSelectedKeys: [] as any[]
    }
  }

  // Transfer species columns
  transferLanguagesColumns = {
    left: [
      {
        dataIndex: 'name',
        title: 'Language',
        searchable: true
      }
    ],
    right: [
      {
        dataIndex: 'name',
        title: 'Language'
      },
      {
        dataIndex: 'main_language',
        title: 'Main Language',
        render: (text: any, record: any) => (
          <Switch
            onChange={(value: boolean) => this.handleMainLanguage(record.key, value)} checked={this.state.mainLanguage === record.key} 
          />
        )
      }
    ]
  }

  componentDidMount() {
    const { loadCountries, loadLanguages } = this.props

    loadCountries({
      options: {
        limit:9999,
      }
    })

    loadLanguages({
      options: {
        limit:9999,
      }
    })
  }

  handleSlug = (name: string, ev: any) => {
    const { form } = this.props
    const value = ev.target.value

    if (name === 'name' && form) {
      form.ref.setFields([
        {
          name: 'slug',
          value: slugify(value.toLowerCase())
        }
      ])
    }

    return value
  }

  handleMainLanguage = (key: string, value: boolean) => {
    const { form } = this.props

    this.setState({
      mainLanguage: value ? key : ''
    }, () => {
      // Create/update a form value for the main language uuid
      form.ref.setFields([
        {
          name: 'main_language',
          value: value ? key : null
        }
      ])
      form.ref.validateFields(['languages'])
    })
  }

  handleSelectionChange = (sourceSelectedKeys: string[], targetSelectedKeys: string[]) => {
    this.setState(state => ({
      ...state,
      selectionLanguages: {
        sourceSelectedKeys,
        targetSelectedKeys
      }
    }))
  }

  handleLanguagesSelection = (targetKeys: string[], direction: any, moveKeys: any) => {
    this.setState({
      selectedLanguages: targetKeys
    })
  }

  render() {
    const { contentLoading, countries, languages, validSlug, verifySlug } = this.props
    const { selectedLanguages } = this.state

    if (contentLoading) {
      return <Skeleton />
    }

    const countriesOptions = countries?.map(country => {
      return {
        label: country.name,
        value: country.uuid
      }
    })

    const languageOptions = languages?.map(lang => {
      const selected = selectedLanguages.includes(lang.uuid)

      return {
        key: lang.uuid,
        name: `${lang.name} (${lang.abbreviation})`,
        main_language: false,
        chosen: selected !== undefined
      }
    }) || []

    return (
      <Row gutter={24}>
        <Col xs={24} sm={10} md={10}>
          <Form.Item
            label="Shelter Name"
            name="name"
            rules={[
              {
                required: true,
                message: 'Required field'
              }
            ]}
            getValueFromEvent={(ev) => this.handleSlug('name', ev)}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Slug Slug code. The code that goes in the public page url. Example: “cat-adoption-team”"
            name="slug"
            rules={[
              {
                required: true,
                message: 'Required field'
              },
              () => ({
                async validator(_, value) {
                  const valid = await verifySlug({ organization_shelter_slug: value })
                  if (valid) return Promise.resolve()

                  return Promise.reject(new Error('Slug is not valid or already exists!'))
                }
              })
            ]}
          >
            <Input disabled={validSlug === null} />
          </Form.Item>

          <Form.Item
            label="Country"
            name="country_uuid"
            rules={[
              {
                required: true,
                message: 'Please select a country'
              }
            ]}
          >
            <Select
              placeholder="Select a country"
              options={countriesOptions}
            />
          </Form.Item>

          <Form.Item
            label="501(c) 3 (EIN#)"
            name="key501c3"
            rules={[
              {
                required: true,
                message: 'Required field'
              },
              {
                max: 10,
                message: 'Max 10 characters'
              }
            ]}
          >
            <Input maxLength={10} />
          </Form.Item>

          <Space direction="horizontal" size={24}>

            <Form.Item
              label="Active"
              name="active"
              valuePropName="checked"
            >
              <Switch defaultChecked={true} />
            </Form.Item>
          </Space>
        </Col>

        <Col xs={24} sm={14} md={14}>
          <Form.Item
            label="Shelter description. Displays on the organization’s public page"
            name="description"
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Space direction="horizontal" size={24}>
            <Form.Item
              label="Shelter main email"
              name={['contact', 'email']}
              rules={[
                {
                  required: true,
                  message: 'Required field'
                },
                {
                  type: 'email',
                  message: 'Invalid email'
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Space>

          <Form.Item
            label="Languages"
            name="languages"
            rules={[
              {
                required: true,
                message: 'Required field'
              },
              () => ({
                validator: (_, value: string[]) => {
                  const { mainLanguage } = this.state
                  if (value.length > 0 && value.includes(mainLanguage)) {
                    return Promise.resolve()
                  }

                  return Promise.reject(new Error('Must select one main language'))
                }
              })
            ]}
          >
            <TableTransfer
              dataSource={languageOptions}
              targetKeys={selectedLanguages}
              listStyle={null}
              leftColumns={this.transferLanguagesColumns.left}
              rightColumns={this.transferLanguagesColumns.right}
              onChange={this.handleLanguagesSelection}
              onSelectChange={(sourceSelectedKeys: string[], targetSelectedKeys: string[]) => this.handleSelectionChange(sourceSelectedKeys, targetSelectedKeys)}
            />
          </Form.Item>
        </Col>
      </Row>
    )
  }
}
